// src/pages/personal/analytics/CustomerSection/components/LtvAnalysis.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/Layout';
import { COLORS } from '@/theme';
import LtvChart from './LtvChart';
import NewClientsChart from './NewClientsChart';

import styles from '../CustomerSection.module.css';

interface FrequencyByStatus {
  status: string;
  name: string;
  average_frequency: number;
  clients_count: number;
}

interface NewClientsTrend {
  month: string; // '2023-01'
  month_name: string; // 'Jan 2023'
  count: number;
}

interface LtvAnalysisProps {
  data: FrequencyByStatus[];
  newClientsData: NewClientsTrend[];
  isLoading?: boolean;
}

// Тип для режимов отображения
type ChartMode = 'ltv' | 'newClients';

const LtvAnalysis: React.FC<LtvAnalysisProps> = ({ 
  data, 
  newClientsData,
  isLoading = false 
}) => {
  const { t } = useTranslation('customer');
  const [activeTab, setActiveTab] = useState<ChartMode>('ltv');
  const [chartKey, setChartKey] = useState<number>(0);
  const [isChartSelectorOpen, setIsChartSelectorOpen] = useState(false);
  
  // Refs для элементов DOM
  const chartButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Принудительно обновляем компонент при смене вкладки
  useEffect(() => {
    console.log("Активная вкладка изменилась на:", activeTab);
    // Увеличиваем ключ чтобы React пересоздал компонент
    setChartKey(prev => prev + 1);
  }, [activeTab]);
  
  // Функция для получения цвета статуса
  const getStatusColor = (status: string): string => {
    const safeKey = status.toLowerCase() as keyof typeof COLORS.clientChartColors;
    return COLORS.clientChartColors?.[safeKey] || COLORS.primary;
  };
  
  // Подготовка данных для графика с добавлением цвета
  const chartData = data.map(item => ({
    name: t(`statuses.${item.status}`, { defaultValue: item.name }),
    value: item.average_frequency,
    clients: item.clients_count,
    color: getStatusColor(item.status),
    status: item.status // Добавляем оригинальный статус для более точной обработки
  }));

  // Структура для селектора режимов отображения
  const chartModes = [
    { value: 'ltv', label: t('ltv_analysis.tab') },
    { value: 'newClients', label: t('new_clients.tab') }
  ];
  
  // Текущий выбранный режим
  const currentChartMode = chartModes.find(m => m.value === activeTab) || chartModes[0];
  
  // Обработчик изменения режима отображения
  const handleChartModeChange = (newMode: ChartMode) => {
    console.log("Выбран режим:", newMode);
    setActiveTab(newMode);
    setIsChartSelectorOpen(false);
  };
  
  // Обработчик клика для открытия/закрытия селектора
  const toggleChartSelector = () => {
    setIsChartSelectorOpen(prev => !prev);
  };
  
  // Упрощенное решение для закрытия селектора при клике в другом месте
  useEffect(() => {
    if (!isChartSelectorOpen) return;
    
    const handleClickOutside = (e: any) => {
      // Проверяем, что клик был вне кнопки и выпадающего списка
      if (
        chartButtonRef.current && 
        !chartButtonRef.current.contains(e.target) &&
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target)
      ) {
        setIsChartSelectorOpen(false);
      }
    };
    
    // @ts-ignore
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      // @ts-ignore
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isChartSelectorOpen]);
  
  // Получение заголовка в зависимости от активной вкладки
  const getTitle = () => {
    return activeTab === 'ltv' 
      ? t('ltv_analysis.title') 
      : t('new_clients.title');
  };
  
  // Проверка наличия данных для текущей вкладки
  const hasData = activeTab === 'ltv' 
    ? chartData.length > 0 
    : newClientsData.length > 0;
    
  // Компонент для отображения загрузки
  const renderLoader = () => (
    <div className={styles.loadingContainer}>
      <div className={styles.loadingSpinner}></div>
      <p className={styles.loadingText}>{t('loading')}</p>
    </div>
  );
  
  // Компонент для отображения отсутствия данных
  const renderNoData = () => (
    <div className={styles.noDataContainer}>
      <p>{t('no_data')}</p>
    </div>
  );
  
  // Рендер контента в зависимости от состояния
  const renderContent = () => {
    if (isLoading) {
      return renderLoader();
    }
    
    if (!hasData) {
      return renderNoData();
    }
    
    // Выбираем класс контейнера в зависимости от активной вкладки
    const containerClass = activeTab === 'ltv' 
      ? styles.chartContainer 
      : styles.newClientsChartContainer;
    
    return (
      <div className={containerClass} key={chartKey}>
        {activeTab === 'ltv' ? (
          <LtvChart data={chartData} />
        ) : (
          <NewClientsChart data={newClientsData} />
        )}
      </div>
    );
  };
  
  return (
    <Card padding="none" shadow="sm" className="w-full">
      <Card.Header className={styles.cardHeader}>
        <div className="flex justify-between items-center">
          <h3 className={styles.cardTitle}>{getTitle()}</h3>
          
          {/* Селектор режимов отображения графика */}
          <div className="relative inline-block">
            <button
              ref={chartButtonRef}
              className="px-4 py-2 bg-white border border-gray-200 rounded-md shadow-sm flex items-center text-sm font-medium text-gray-700"
              onClick={toggleChartSelector}
            >
              <span>{currentChartMode.label}</span>
              <svg
                className="ml-2 h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            
            {isChartSelectorOpen && (
              <div 
                ref={dropdownRef}
                className="absolute right-0 mt-1 w-60 bg-white rounded-md shadow-lg z-10 border border-gray-200"
              >
                <ul className="py-1">
                  {chartModes.map((mode) => (
                    <li key={mode.value}>
                      <button
                        type="button"
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                          mode.value === activeTab ? 'bg-gray-50 font-medium text-[#245D33]' : 'text-gray-700'
                        }`}
                        onClick={() => {
                          console.log("Клик по элементу меню:", mode.value);
                          handleChartModeChange(mode.value as ChartMode);
                        }}
                      >
                        {mode.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </Card.Header>
      <Card.Body className={styles.cardContent}>
        {renderContent()}
      </Card.Body>
    </Card>
  );
};

export default LtvAnalysis;